/**
 * HeadingAnchor — TipTap Extension
 *
 * Adds auto-generated `id` attributes to heading elements based on their text
 * content (slugified). This enables in-document anchor links like `#einleitung`.
 *
 * Uses a ProseMirror plugin (decorations) to keep IDs in sync without
 * modifying the document schema.
 */

import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

/** Slugify a heading string → safe anchor id */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[äÄ]/g, 'ae').replace(/[öÖ]/g, 'oe').replace(/[üÜ]/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/^-+|-+$/g, '')
    || 'heading'
}

/** Extract all headings from the current editor doc */
export function extractHeadings(doc: any): { level: number; text: string; slug: string; pos: number }[] {
  const headings: { level: number; text: string; slug: string; pos: number }[] = []
  const slugCount: Record<string, number> = {}

  doc.descendants((node: any, pos: number) => {
    if (node.type.name === 'heading') {
      const text = node.textContent
      const baseSlug = slugify(text)
      // Deduplicate slugs
      slugCount[baseSlug] = (slugCount[baseSlug] || 0) + 1
      const slug = slugCount[baseSlug] > 1 ? `${baseSlug}-${slugCount[baseSlug]}` : baseSlug
      headings.push({ level: node.attrs.level, text, slug, pos })
    }
  })

  return headings
}

const pluginKey = new PluginKey('headingAnchor')

export const HeadingAnchor = Extension.create({
  name: 'headingAnchor',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: pluginKey,
        props: {
          decorations(state) {
            const { doc } = state
            const decorations: Decoration[] = []
            const slugCount: Record<string, number> = {}

            doc.descendants((node, pos) => {
              if (node.type.name === 'heading') {
                const text = node.textContent
                const baseSlug = slugify(text)
                slugCount[baseSlug] = (slugCount[baseSlug] || 0) + 1
                const slug = slugCount[baseSlug] > 1 ? `${baseSlug}-${slugCount[baseSlug]}` : baseSlug
                decorations.push(
                  Decoration.node(pos, pos + node.nodeSize, { id: slug })
                )
              }
            })

            return DecorationSet.create(doc, decorations)
          },
        },
      }),
    ]
  },
})
